import React, { FC, useEffect, useState } from 'react'
import { IS_PROD } from '../../utils/constants'
import { useForm, FormProvider, ErrorOption } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import useFormPersist from 'react-hook-form-persist'
import { useLocation } from '@reach/router'
import { navigate } from 'gatsby'
import { v4 as uuidv4 } from 'uuid'
// UI
import FormField from './FormField'
import CallToAction, { CallToActionType } from '../CallToAction'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'
import { InlineAlert } from './InlineAlert'
import FeedbackMessage, { FeedbackContent, ICustomIcons } from './FeedbackMessage'
import ErrorSmall from '../static/ErrorSmall.svg'
import SuccessSmall from '../static/SuccessSmall.svg'
// Types
import { IForm } from './types'

// Utils
import getValidationSchema from './utils/getValidationSchema'
import { getDefaultValues } from './utils/getDefaultValues'
import { transformPayload } from './utils/transformPayload'
import { getUpdatedFields } from './utils/getUpdatedFields'
import trackingEvent, { FormInfo } from '../../utils/trackingEvent'
import { IAlertBanner } from '../AlertBanner'

export type FormProps = Omit<IForm, 'usabillaId' | 'internal' | 'formId'> & {
  internal?: { type: string }
  formId: string | ''
  uuid?: string
  isDialogForm?: boolean
  formState?: (data: boolean) => void
  alertText?: string
  usabillaId?: string
  showDialogButtons?: (data: boolean) => boolean
  onResponseData?: (data: unknown) => void
}

const Form: FC<FormProps> = ({
  title,
  formId,
  description,
  fields,
  endpoint,
  contentful_id,
  inlineAnnouncement,
  submitButton,
  buttonTextSending,
  errorMessage,
  successMessage,
  uuid,
  isDialogForm = false,
  persistState,
  formState,
  alertText = 'Der er felter, som ikke er udfyldt korrekt',
  usabillaId,
  showDialogButtons,
  onResponseData,
  isFullWitdh = false,
}) => {
  const [isError, setIsError] = useState<boolean>(false)
  const [hasInitiated, setHasInitiated] = useState<boolean>(false)
  const updatedFields = getUpdatedFields(fields)
  const defaultValues = getDefaultValues(fields)
  const zodSchema = getValidationSchema(updatedFields)
  const { href } = useLocation()
  const [formFeedback, setFormFeedback] = useState<FeedbackContent>(errorMessage)
  const [isShowFeedbackContent, setShowFeedbackContent] = useState<boolean>(true)

  type FormSchemaType = z.infer<typeof zodSchema>

  const endpointUrl = IS_PROD ? endpoint?.productionEndpoint : (endpoint?.stagingEndpoint ?? '')

  const form = useForm<FormSchemaType>({
    resolver: zodResolver(zodSchema),
    defaultValues,
    mode: 'all',
  })
  const hasFieldErrors = Object.keys(form.formState.errors).length > 0
  const { reset, watch, setValue } = form

  // PersistState
  persistState &&
    useFormPersist(`${contentful_id}`, {
      watch: watch,
      setValue: setValue,
      exclude: ['date', 'time', 'password'],
    })

  const { linkTarget: _linkTarget, ...submitButtonProps } = submitButton // We don't want linktarget on the button, since it should not redirect anywhere.

  const options = endpoint?.options ? JSON.parse(endpoint?.options) : null
  const method = endpoint?.options ? endpoint.method : 'POST'

  const responseIsFeedbackContent = (response: Record<string, unknown>): boolean => {
    return 'title' in response || 'description' in response || 'buttonLabel' in response
  }

  const onSubmit = async (data: FormSchemaType) => {
    if (!endpointUrl) {
      throw new Error('Endpoint is missing')
    }
    showDialogButtons && showDialogButtons(false)

    // Validate confirmation fields
    updatedFields.forEach(async field => {
      if (field.name.startsWith('confirmation-')) {
        if (data[field.name] !== data[field.name.replace('confirmation-', '')]) {
          form.setError(field.name, {
            type: field.name,
            message: 'Udfyld som ovenstående',
          })
          form.formState.errors[field.name] = {
            type: field.name,
            message: 'Udfyld som ovenstående',
          }
        } else {
          form.clearErrors(field.name)
        }
      }
    })
    // reject submission if errors exist
    if (Object.keys(form.formState.errors).length !== 0) return

    try {
      // Form submission if formbody is passed through url params
      if (endpoint?.postAsUrlQuery) {
        const formBody = transformPayload(data, fields, true)
        const urlParams = new URLSearchParams()
        for (const key in formBody) {
          urlParams.append(key, formBody[key])
        }
        const response = await fetch(`${endpointUrl}?${urlParams.toString()}`, {
          ...(options && options),
          method,
        })
        if (!response.ok) {
          const apiResponse = await response.json()
          responseIsFeedbackContent(apiResponse) && setFormFeedback({ ...errorMessage, ...apiResponse })
          throw new Error(response.statusText)
        }
        if (response.type === 'cors' && !!response.url) {
          navigate(response.url)
        }
        setFormFeedback(successMessage)
        return
      }

      // Regular form submission
      const formPayload = transformPayload(data, fields)
      const response = await fetch(endpointUrl, {
        ...(options && options),
        method,
        headers: {
          'x-formid': formId,
        },
        body: JSON.stringify(formPayload),
      })
      const apiResponse = await response.json()
      if (!response.ok) {
        responseIsFeedbackContent(apiResponse) && setFormFeedback({ ...errorMessage, ...apiResponse })
        throw new Error(response.statusText)
      }
      if (onResponseData) onResponseData(apiResponse)
      if (apiResponse?.redirectUri) setShowFeedbackContent(false)
      setFormFeedback(successMessage)
    } catch (error: unknown) {
      form.setError('root', error as ErrorOption)
      console.error(error)
    }
  }

  useEffect(() => {
    setIsError(Boolean(form.formState.errors.root))
  }, [form.formState.errors.root])

  const clearFormError = () => {
    delete form.formState.errors.root
    showDialogButtons && showDialogButtons(true)
    setIsError(false)
  }

  useEffect(() => {
    formState && formState(form.formState.isSubmitting)
  }, [form.formState.isSubmitting])

  const customIcons: ICustomIcons = {
    CustomErrorIcon: ErrorSmall,
    CustomSuccessIcon: SuccessSmall,
  }

  // Adobe Analytics - Form Tracking
  const formName = formId ? formId : 'Untitled-form-' + uuidv4()
  const formType = isDialogForm ? 'popup form' : 'inline form'

  // Form initiate
  const formInitiateTracking = () => {
    if (hasInitiated) return
    const formInfo: FormInfo = {
      name: formName,
      type: formType,
    }
    trackingEvent({ event: 'form_initiate', formInfo })
    setHasInitiated(true)
  }

  // Form submission
  useEffect(() => {
    if (form.formState.isSubmitted === false) return
    const formInfo: FormInfo = {
      name: formName,
      formSubmission: form.formState.isSubmitSuccessful ? 'successful' : 'failed',
      fullUrl: href,
      type: formType,
    }
    trackingEvent({ event: 'form_submit', formInfo })
  }, [form?.formState?.isSubmitted])

  if (form.formState.isSubmitSuccessful && isShowFeedbackContent) {
    return (
      <FeedbackMessage
        usabillaId={usabillaId}
        content={{
          ...formFeedback,
          onClick: () => {
            showDialogButtons && showDialogButtons(true)
            return reset()
          },
        }}
        {...(uuid ? { customIcons: customIcons } : {})}
      />
    )
  }

  if (isError && form.formState.isSubmitted) {
    return (
      <FeedbackMessage
        isError
        content={{ ...formFeedback, onClick: clearFormError }}
        {...(uuid ? { customIcons: customIcons } : {})}
      />
    )
  }

  const styles = {
    inlineAlert: {
      marginBottom: '12px',
    },
  }

  return (
    <Box
      sx={{
        width: isFullWitdh ? '100%' : '310px',
        maxWidth: '100%',
        paddingTop: '10px',
      }}
    >
      {title && (
        <Typography variant="h5" component="h2" sx={{ mb: 2 }}>
          {title}
        </Typography>
      )}
      {description && (
        <Typography variant="body2" component="p" sx={{ mb: 2 }}>
          {description}
        </Typography>
      )}
      {inlineAnnouncement && <InlineAlert {...(inlineAnnouncement as IAlertBanner)} />}
      <FormProvider {...form}>
        <form
          id={uuid ? uuid : contentful_id}
          onSubmit={form.handleSubmit(onSubmit)}
          noValidate
          onFocus={formInitiateTracking}
        >
          {updatedFields &&
            updatedFields.map(field => <FormField key={field!.id!} {...field!} control={form.control} />)}
          {hasFieldErrors && (
            <Alert sx={styles.inlineAlert} variant="inline" severity="error">
              {alertText}
            </Alert>
          )}
          {!isDialogForm && (
            <CallToAction
              {...(submitButtonProps as CallToActionType)}
              {...(form.formState.isSubmitting && buttonTextSending && { buttonText: buttonTextSending })}
              id={`submit-button-${submitButtonProps?.contentful_id}`}
              type="submit"
              disabled={form.formState.isSubmitting}
              startIcon={form.formState.isSubmitting && <CircularProgress color="inherit" size={16} />}
            />
          )}
        </form>
      </FormProvider>
    </Box>
  )
}

export default Form
